import { z } from 'zod';
import type { ParseResult } from '../voyage/schemas.js';
import { type PdiTreeArea, pdiLevelOf } from './library.js';
import {
  optionalParagraph,
  optionalText,
  optionalUuid,
  parsed,
  requiredText,
  typed,
} from './schema.js';

/**
 * Pre-Docking denetim sonucu (ADR 0129 Ek Faz 2; Excel "V1.2 Form Field Schema" Inspection,
 * "Software Workflow" 2–5). Gemi verisidir; sonuç satırı değiştirilemez, düzeltme yeni revizyondur.
 * Saf modül: saat parametreyle gelir.
 *
 * - "Not Inspected" saklanan bir sonuç değildir: noktada sonuç yoksa durum odur (Excel "Not
 *   Started").
 * - Erişilemeyen ya da tersane sonrasına kalan nokta yalnız "Deferred – After Docking" olur ve
 *   After Docking kuyruğunda pending kalır (Excel "Inaccessible"; R5).
 * - Olumsuz sonuçta not, N/A ve ertelemede gerekçe zorunlu (Excel "Mandatory if adverse", "N/A
 *   requires reason").
 * - Ölçümde sınır yoksa pass/fail verilmez (Excel "No pass/fail without limit").
 */

export const PDI_ACCESS = ['accessible', 'inaccessible', 'after_docking'] as const;
export type PdiAccess = (typeof PDI_ACCESS)[number];

export const PDI_RESULTS = [
  'satisfactory',
  'attention_required',
  'unsatisfactory',
  'not_applicable',
  'deferred',
] as const;
export type PdiResult = (typeof PDI_RESULTS)[number];

/** Olumsuz sonuçlar: not zorunlu, ilerlemede "Adverse". */
export const PDI_ADVERSE: readonly PdiResult[] = ['attention_required', 'unsatisfactory'];

/** Gemi profili (Excel "Applicability"): satır yoksa `review`. */
export const PDI_APPLICABILITY = ['applicable', 'not_applicable', 'review'] as const;
export type PdiApplicability = (typeof PDI_APPLICABILITY)[number];

/** Saat kayması payı: istemci saati gemi sunucusunun birkaç dakika önünde olabilir. */
const FUTURE_SKEW_MS = 5 * 60 * 1000;
const MAX_MEASUREMENTS = 20;

const finite = (code: string) =>
  z.number(typed(code)).refine((value) => Number.isFinite(value), code);
const optionalNumber = z
  .union([finite('INVALID_NUMBER'), z.literal('')], typed('INVALID_NUMBER'))
  .nullish()
  .transform((value) => (value === '' || value === undefined ? null : value));

export const pdiMeasurementSchema = z
  .strictObject({
    label: requiredText(120),
    value: finite('INVALID_NUMBER'),
    unit: optionalText(40),
    /** Alt / üst sınır (tolerans): yoksa karar verilmez. */
    lower: optionalNumber,
    upper: optionalNumber,
    /** Kabul ölçütünün kaynağı (üretici, klas, çizim). */
    reference: optionalText(200),
  })
  .superRefine((value, ctx) => {
    if (value.lower !== null && value.upper !== null && value.lower > value.upper) {
      ctx.addIssue({ code: 'custom', path: ['upper'], message: 'LIMITS_REVERSED' });
    }
  });
export type PdiMeasurement = z.output<typeof pdiMeasurementSchema>;

const ISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})$/;
const instant = z
  .string(typed('INVALID_DATE'))
  .regex(ISO, 'INVALID_DATE')
  .refine((value) => !Number.isNaN(new Date(value).getTime()), 'INVALID_DATE')
  .transform((value) => new Date(value).toISOString());

export interface PdiResultOptions {
  /** Gemi sunucusu saati (ISO-8601 UTC): denetim zamanı bundan sonra olamaz. */
  now: string;
  /** Yazılacak revizyon; 2 ve sonrası düzeltmedir, gerekçe ister. */
  revision?: number;
}

function resultSchema(options: PdiResultOptions) {
  return z
    .strictObject({
      checkpointCode: z
        .string(typed('NOT_CHECKPOINT'))
        .transform((value) => value.trim())
        .refine((value) => pdiLevelOf(value) === 'checkpoint', 'NOT_CHECKPOINT'),
      /** İsteğe bağlı PMS ekipmanı (Excel "Equipment ID"). */
      equipmentId: optionalUuid,
      access: z.enum(PDI_ACCESS, typed('INVALID_OPTION')),
      result: z.enum(PDI_RESULTS, typed('INVALID_OPTION')),
      /** Koşul notu; olumsuzda zorunlu, N/A ve ertelemede gerekçe. */
      notes: optionalParagraph(4000),
      measurements: z
        .array(pdiMeasurementSchema, typed('INVALID'))
        .max(MAX_MEASUREMENTS, 'TOO_MANY')
        .nullish()
        .transform((value) => value ?? []),
      /** Denetim zamanı (UTC); gemi saati farkı yazma yolundan (R7). */
      inspectedAt: instant,
      /** Denetçi (Excel "Inspector": crew; varsayılan oturumdaki kişi). */
      inspectorId: z.uuid(typed('INVALID_ID')),
      /** Düzeltmenin gerekçesi (2. ve sonraki revizyon). */
      correctionReason: optionalParagraph(1000),
    })
    .superRefine((value, ctx) => {
      const issue = (path: string, message: string) =>
        ctx.addIssue({ code: 'custom', path: [path], message });
      if (value.access !== 'accessible' && value.result !== 'deferred') {
        issue('result', 'ACCESS_REQUIRES_DEFERRED');
      }
      if (PDI_ADVERSE.includes(value.result) && !value.notes) issue('notes', 'NOTES_REQUIRED');
      if ((value.result === 'not_applicable' || value.result === 'deferred') && !value.notes) {
        issue('notes', 'REASON_REQUIRED');
      }
      if (
        new Date(value.inspectedAt).getTime() >
        new Date(options.now).getTime() + FUTURE_SKEW_MS
      ) {
        issue('inspectedAt', 'FUTURE_DATE');
      }
      if ((options.revision ?? 1) > 1 && !value.correctionReason) {
        issue('correctionReason', 'REQUIRED');
      }
    });
}
export type PdiResultInput = z.output<ReturnType<typeof resultSchema>>;

export function parsePdiResult(
  input: unknown,
  options: PdiResultOptions,
): ParseResult<PdiResultInput> {
  return parsed(resultSchema(options).safeParse(input));
}

/** Ölçümün kararı: sınır yoksa `null` (pass/fail verilmez); sınır dahil kabul. */
export function pdiMeasurementVerdict(m: {
  value: number;
  lower: number | null;
  upper: number | null;
}): 'pass' | 'fail' | null {
  if (m.lower === null && m.upper === null) return null;
  if (m.lower !== null && m.value < m.lower) return 'fail';
  if (m.upper !== null && m.value > m.upper) return 'fail';
  return 'pass';
}

export const pdiApplicabilitySchema = z
  .strictObject({
    applicability: z.enum(PDI_APPLICABILITY, typed('INVALID_OPTION')),
    reason: optionalParagraph(1000),
  })
  .superRefine((value, ctx) => {
    if (value.applicability === 'not_applicable' && !value.reason) {
      ctx.addIssue({ code: 'custom', path: ['reason'], message: 'REASON_REQUIRED' });
    }
  });
export type PdiApplicabilityInput = z.output<typeof pdiApplicabilitySchema>;

export function parsePdiApplicability(input: unknown): ParseResult<PdiApplicabilityInput> {
  return parsed(pdiApplicabilitySchema.safeParse(input));
}

/** Noktanın ekrandaki durumu: gemi N/A, sonuç yok (Not Inspected) ya da son sonuç. */
export type PdiCheckpointStatus = PdiResult | 'not_inspected' | 'vessel_na';

export function pdiCheckpointStatus(
  latest: PdiResult | undefined,
  vesselNotApplicable: boolean,
): PdiCheckpointStatus {
  if (vesselNotApplicable) return 'vessel_na';
  return latest ?? 'not_inspected';
}

export interface PdiProgressCounts {
  /** Gemi profilinde N/A olmayan noktalar. */
  applicable: number;
  /** Sonucu olan (ertelenen hariç) noktalar. */
  done: number;
  /** Sonucu olmayan ya da ertelenen noktalar (`applicable - done`). */
  remaining: number;
  /** Attention Required ya da Unsatisfactory. */
  adverse: number;
  /** After Docking kuyruğu: son sonucu Deferred. */
  deferred: number;
  /** Gemi profilinde N/A ya da sonucu N/A. */
  notApplicable: number;
}

export interface PdiProgress {
  total: PdiProgressCounts;
  areas: Map<string, PdiProgressCounts>;
  systems: Map<string, PdiProgressCounts>;
}

const zero = (): PdiProgressCounts => ({
  applicable: 0,
  done: 0,
  remaining: 0,
  adverse: 0,
  deferred: 0,
  notApplicable: 0,
});

/**
 * İlerleme sayıları (alan / sistem / toplam): yapılan, kalan, olumsuz, After Docking ve N/A. Gemi
 * profilinde N/A olan nokta sayıma girmez (R6); ertelenen nokta kalan sayılır ve pending kalır.
 */
export function pdiProgress(
  tree: readonly PdiTreeArea[],
  latest: ReadonlyMap<string, PdiResult>,
  vesselNotApplicable: ReadonlySet<string>,
): PdiProgress {
  const total = zero();
  const areas = new Map<string, PdiProgressCounts>();
  const systems = new Map<string, PdiProgressCounts>();
  for (const area of tree) {
    const a = zero();
    for (const system of area.systems) {
      const s = zero();
      for (const checkpoint of system.checkpoints) {
        const result = latest.get(checkpoint.code);
        const add = (key: keyof PdiProgressCounts) => {
          s[key] += 1;
          a[key] += 1;
          total[key] += 1;
        };
        if (vesselNotApplicable.has(checkpoint.code)) {
          add('notApplicable');
          continue;
        }
        add('applicable');
        if (result === undefined || result === 'deferred') add('remaining');
        else add('done');
        if (result === 'deferred') add('deferred');
        if (result !== undefined && PDI_ADVERSE.includes(result)) add('adverse');
        if (result === 'not_applicable') add('notApplicable');
      }
      systems.set(system.code, s);
    }
    areas.set(area.code, a);
  }
  return { total, areas, systems };
}
